import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppNotification, NotificationConfig, NotificationConfigListResponse, NotificationListResponse, UnreadCountResponse, UpdateNotificationConfigBody } from '../models/notification';
import { ApiService } from './api.service';

/**
 * Comunicación HTTP con /notifications (notificaciones persistentes del Backend, que es la fuente
 * de verdad) y contador de no leídas compartido por la campana y la pantalla de notificaciones.
 *
 * Los métodos rechazan la promesa si la petición falla: cada consumidor decide cómo mostrar el
 * error. No incluye navegación.
 */
@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private readonly path = `${environment.api.v1.path}${environment.api.v1.urls.notifications}`;

  // null = todavía desconocido (o nunca se pudo consultar): la campana no muestra un número inventado.
  private unreadCountSubject = new BehaviorSubject<number | null>(null);
  unreadCount$ = this.unreadCountSubject.asObservable();

  constructor(private http: HttpClient, private apiServ: ApiService) { }

  private options() {
    return { headers: new HttpHeaders({ Authorization: `Bearer ${this.apiServ.doGetToken()}` }) };
  }

  getNotifications(page = 1, limit = 20, unread?: boolean): Promise<NotificationListResponse> {
    const query = `page=${page}&limit=${limit}${unread ? '&unread=true' : ''}`;
    return lastValueFrom(this.http.get<NotificationListResponse>(`${this.path}?${query}`, this.options()));
  }

  getNotification(id: string): Promise<AppNotification> {
    return lastValueFrom(this.http.get<AppNotification>(`${this.path}/${id}`, this.options()));
  }

  async markAsRead(id: string): Promise<AppNotification> {
    const notification = await lastValueFrom(this.http.put<AppNotification>(`${this.path}/${id}/read`, {}, this.options()));
    this.refreshUnreadCount();
    return notification;
  }

  async markAllAsRead(): Promise<{ modified: number }> {
    const result = await lastValueFrom(this.http.put<{ modified: number }>(`${this.path}/read-all`, {}, this.options()));
    this.refreshUnreadCount();
    return result;
  }

  // Configuración administrativa: el Backend exige el permiso actionsNotificationsConfig
  getConfig(): Promise<NotificationConfigListResponse> {
    return lastValueFrom(this.http.get<NotificationConfigListResponse>(`${this.path}/config`, this.options()));
  }

  updateConfig(type: string, body: UpdateNotificationConfigBody): Promise<NotificationConfig> {
    return lastValueFrom(this.http.put<NotificationConfig>(`${this.path}/config/${type}`, body, this.options()));
  }

  getUnreadCount(): Promise<UnreadCountResponse> {
    return lastValueFrom(this.http.get<UnreadCountResponse>(`${this.path}/unread-count`, this.options()));
  }

  /**
   * Consulta el contador al Backend y lo publica en unreadCount$. Si falla conserva el último valor
   * conocido (nunca inventa uno) y no propaga el error: un fallo del contador no debe romper el Header.
   */
  async refreshUnreadCount(): Promise<void> {
    try {
      const { count } = await this.getUnreadCount();
      this.unreadCountSubject.next(typeof count === 'number' ? count : null);
    } catch (error) {
      console.error('No se pudo obtener el contador de notificaciones no leídas');
    }
  }

  // Al cerrar sesión / cambiar de usuario se descarta el contador del usuario anterior.
  resetUnreadCount(): void {
    this.unreadCountSubject.next(null);
  }
}
